// Supabase service for MoZo.Live
// Optimized for low-quality cell phone connections

import { supabase } from './supabase';
import { supabaseAdmin } from './services/supabase-admin';
import { User } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 500; // ms

// Helper function for retry logic with exponential backoff
export async function retryOperation<T>(operation: () => Promise<T>, retries = MAX_RETRIES, delay = RETRY_DELAY): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (retries <= 0) throw error;
    console.log(`Operation failed, retrying in ${delay/1000}s... (${retries} retries left)`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryOperation(operation, retries - 1, delay * 1.5);
  }
}

// Collection/table configuration
export const collections = {
  users: 'users',
  profiles: 'profiles',
  posts: 'posts',
  comments: 'comments',
  stories: 'stories',
  likes: 'likes',
  follows: 'follows',
  bookmarks: 'bookmarks',
  boostedContent: 'boosted_content',
  boostedContentMetrics: 'boosted_content_metrics',
  boostedContentInteractions: 'boosted_content_interactions',
  storyInteractions: 'story_interactions',
};

// Storage bucket configuration for self-hosted Supabase
export const buckets = {
  avatars: 'mozo_avatars',
  posts: 'mozo_posts',
  stories: 'mozo_stories',
  products: 'mozo_products',
  media: 'mozo_media',
  uploads: 'mozo_uploads',
};

// User service with retry logic
export const userService = {
  // Create profiles for users who don't have one
  async createMissingProfiles(): Promise<void> {
    try {
      // Get all users from the profiles table instead of using admin API
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, username, avatar_url, created_at');
      
      if (usersError) {
        console.error('Error fetching users:', usersError);
        return;
      }
      
      if (!users || users.length === 0) {
        console.log('No users found in profiles table');
        return;
      }
      
      // Get all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from(collections.profiles)
        .select('id');
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        return;
      }
      
      // Define interface for profile data
      interface ProfileData {
        id: string;
        username?: string;
        avatar_url?: string;
        created_at?: string;
      }

      // Find users without profiles
      const profileIds = profiles?.map(profile => profile.id) || [];
      // Since we're now getting profiles directly, we need to check which IDs are missing
      const usersWithoutProfiles: ProfileData[] = users?.filter((user: ProfileData) => !profileIds.includes(user.id)) || [];
      
      if (usersWithoutProfiles.length === 0) {
        console.log('All users have profiles');
        return;
      }
      
      // Create profiles for users without them
      const profilesToCreate = usersWithoutProfiles.map((user: ProfileData) => ({
        id: user.id,
        username: user.username || `user_${Math.floor(Math.random() * 10000)}`,
        avatar_url: user.avatar_url || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      
      const { error: insertError } = await supabase
        .from(collections.profiles)
        .insert(profilesToCreate);
      
      if (insertError) {
        console.error('Error creating profiles:', insertError);
        return;
      }
      
      console.log(`Created ${profilesToCreate.length} profiles`);
    } catch (error) {
      console.error('Error in createMissingProfiles:', error);
    }
  },
  
  // Get current user
  async getCurrentUser(): Promise<User | null> {
    try {
      // First check if we have a session
      const { data: sessionData } = await supabase.auth.getSession();
      
      // If no session, don't even try to get the user to avoid AuthSessionMissingError
      if (!sessionData.session) {
        return null;
      }
      
      // We have a session, so we can safely get the user
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        // Check if it's an AuthSessionMissingError
        if (error.message?.includes('Auth session missing')) {
          // Session might have expired, try to refresh
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError || !refreshData.session) {
            console.log('Session refresh failed, user needs to login again');
            return null;
          }
          
          // Return the refreshed user
          return refreshData.user;
        }
        
        console.error('Error getting current user:', error);
        return null;
      }
      
      return user;
    } catch (error) {
      // Suppress excessive logging of AuthSessionMissingError
      if (error instanceof Error && !error.message?.includes('Auth session missing')) {
        console.error('Error in getCurrentUser:', error);
      }
      return null;
    }
  },
  
  // Get user profile
  async getUserProfile(userId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.profiles)
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error getting user profile:', error);
        throw error;
      }
      
      return data;
    });
  },
  
  // Track profile view
  async trackProfileView(profileId: string) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || user.id === profileId) {
        // Don't track views of your own profile
        return;
      }
      
      const { error } = await supabase.rpc('increment_profile_views', {
        profile_id: profileId
      });
      
      if (error) {
        console.error('Error tracking profile view:', error);
        throw error;
      }
    });
  }
};

// Media service with comprehensive file upload handling and fallbacks
export const mediaService = {
  // Upload file with proper CORS handling and network resilience
  uploadFile(file: File, bucketId: string): Promise<{ url: string; fileId: string }> {
    return retryOperation(async () => {
      // Debug: Log the file object details
      console.log('📁 mediaService.uploadFile - received file:', {
        hasFile: !!file,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
        fileObject: file,
        bucketId
      });
      
      // Generate a unique file name to avoid collisions
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      
      console.log('🔄 mediaService.uploadFile - about to upload:', {
        fileName,
        fileExt,
        fileSize: file.size,
        bucketId
      });
      
      // Upload the file using regular authenticated client
      const { data, error } = await supabase.storage
        .from(bucketId)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
      
      // Get the public URL using regular client
      const { data: { publicUrl } } = supabase.storage
        .from(bucketId)
        .getPublicUrl(data.path);
      
      return {
        url: publicUrl,
        fileId: data.path
      };
    });
  },
  
  // Get file preview with optimized size for mobile
  getFilePreview(bucketId: string, fileId: string, width = 400): string {
    try {
      const { data: { publicUrl } } = supabaseAdmin.storage
        .from(bucketId)
        .getPublicUrl(fileId, {
          transform: {
            width,
            height: width,
            resize: 'cover'
          }
        });
      
      return publicUrl;
    } catch (error) {
      console.error('Error getting file preview:', error);
      return '';
    }
  }
};

// Posts service with retry logic
export const postService = {
  // Get posts for feed with comments and media items
  async getFeedPosts(limit = 10, offset = 0) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to get feed posts');
      }
      
      // Get posts from followed users and the user's own posts
      const { data: posts, error } = await supabase
        .from(collections.posts)
        .select(`
          *,
          users:user_id(*),
          bookmarks:bookmarks(post_id)
        `)
        .order('created_at', { ascending: false })
        .limit(limit)
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error getting feed posts:', error);
        throw error;
      }
      
      if (!posts || posts.length === 0) {
        return [];
      }
      
      // Fetch comments for each post
      const postsWithComments = await Promise.all(posts.map(async (post) => {
        try {
          const comments = await postService.getPostComments(post.id);
          const bookmarkCount = await postService.getPostBookmarkCount(post.id);
          
          // Create mediaItems from both media_url and media_urls fields
          let mediaItems = [];
          
          // Check media_urls array first (newer format)
          if (post.media_urls && Array.isArray(post.media_urls) && post.media_urls.length > 0) {
            mediaItems = post.media_urls.map((mediaUrl: any, index: number) => ({
              id: `media-${post.id}-${index}`,
              url: typeof mediaUrl === 'string' ? mediaUrl : mediaUrl.url || mediaUrl,
              type: post.media_type || 'image',
              thumbnail: typeof mediaUrl === 'string' ? mediaUrl : mediaUrl.url || mediaUrl
            }));
          }
          // Fallback to single media_url field (older format)
          else if (post.media_url) {
            mediaItems = [{
              id: `media-${post.id}`,
              url: post.media_url,
              type: post.media_type || 'image',
              thumbnail: post.media_url
            }];
          }
          
          return {
            ...post,
            comments: comments || [],
            bookmarkCount,
            mediaItems
          };
        } catch (err) {
          console.error(`Error fetching comments for post ${post.id}:`, err);
          
          // Create mediaItems from both media_url and media_urls fields (same logic as above)
          let mediaItems = [];
          
          // Check media_urls array first (newer format)
          if (post.media_urls && Array.isArray(post.media_urls) && post.media_urls.length > 0) {
            mediaItems = post.media_urls.map((mediaUrl: any, index: number) => ({
              id: `media-${post.id}-${index}`,
              url: typeof mediaUrl === 'string' ? mediaUrl : mediaUrl.url || mediaUrl,
              type: post.media_type || 'image',
              thumbnail: typeof mediaUrl === 'string' ? mediaUrl : mediaUrl.url || mediaUrl
            }));
          }
          // Fallback to single media_url field (older format)
          else if (post.media_url) {
            mediaItems = [{
              id: `media-${post.id}`,
              url: post.media_url,
              type: post.media_type || 'image',
              thumbnail: post.media_url
            }];
          }
          
          return {
            ...post,
            comments: [],
            bookmarkCount: 0,
            mediaItems
          };
        }
      }));
      
      return postsWithComments;
    });
  },
  
  // Get post by ID
  async getPost(postId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.posts)
        .select(`
          *,
          users:user_id(*)
        `)
        .eq('id', postId)
        .single();
      
      if (error) {
        console.error('Error getting post:', error);
        throw error;
      }
      
      return data;
    });
  },
  
  // Create post
  async createPost(data: any) {
    return retryOperation(async () => {
      const { data: post, error } = await supabase
        .from(collections.posts)
        .insert(data)
        .select()
        .single();
      
      if (error) {
        console.error('Error creating post:', error);
        throw error;
      }
      
      return post;
    });
  },
  
  // Increment post views
  async incrementPostViews(postId: string) {
    return retryOperation(async () => {
      const { error } = await supabase.rpc('increment_post_views', {
        post_id: postId
      });
      
      if (error) {
        console.error('Error incrementing post views:', error);
        throw error;
      }
      
      return true;
    });
  },
  
  // Get comments for a post
  async getPostComments(postId: string, limit = 10) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          user:profiles(id, username, avatar_url)
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting post comments:', error);
        throw error;
      }

      return data;
    });
  },
  
  // Create a comment
  async createComment(postId: string, userId: string, content: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          user_id: userId,
          content,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating comment:', error);
        throw error;
      }
      
      return data;
    });
  },
  
  // Get bookmark count for a post
  async getPostBookmarkCount(postId: string) {
    return retryOperation(async () => {
      const { count, error } = await supabase
        .from(collections.bookmarks)
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId)
        .not('post_id', 'is', null);
      
      if (error) {
        console.error('Error getting bookmark count:', error);
        return 0;
      }
      
      return count || 0;
    });
  },
};

// Stories service with retry logic
export const storyService = {
  // Get stories
  async getStories(limit = 10) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to get stories');
      }
      
      // Get stories that haven't expired yet
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from(collections.stories)
        .select('*')
        .gt('expires_at', now)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error getting stories:', error);
        throw error;
      }
      
      // Get user profiles for the stories
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(story => story.user_id))];
        
        // Get profiles data
        const { data: profiles } = await supabase
          .from('users')
          .select('id, username, full_name, avatar_url')
          .in('id', userIds);
        
        // Attach user data to stories with better username handling
        const storiesWithUsers = data.map(story => {
          const profile = profiles?.find(profile => profile.id === story.user_id);
          
          // Create user object with proper username fallback
          const user = profile ? {
            ...profile,
            username: profile.username || 
                     profile.full_name || 
                     `user_${story.user_id.slice(-4)}`
          } : {
            id: story.user_id,
            username: `user_${story.user_id.slice(-4)}`,
            full_name: null,
            avatar_url: null
          };
          
          return {
            ...story,
            user
          };
        });
        
        return storiesWithUsers;
      }
      
      return data;
    });
  },
  
  // Get story by ID
  async getStoryById(storyId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.stories)
        .select('*')
        .eq('id', storyId)
        .single();
      
      if (error) {
        console.error('Error getting story:', error);
        throw error;
      }
      
      // Get user profile for the story
      if (data) {
        const { data: profile } = await supabase
          .from('users')
          .select('id, username, full_name, avatar_url')
          .eq('id', data.user_id)
          .single();
        
        return {
          ...data,
          user: profile || null
        };
      }
      
      return data;
    });
  },
  
  // Create story
  async createStory(data: any) {
    return retryOperation(async () => {
      // Set expiration time (24 hours from now)
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
      
      const storyData = {
        ...data,
        expires_at: expiresAt.toISOString(),
        metrics: { views: 0, replies: 0 }
      };
      
      const { data: story, error } = await supabase
        .from(collections.stories)
        .insert(storyData)
        .select('*')
        .single();
      
      if (error) {
        console.error('Error creating story:', error);
        throw error;
      }
      
      return story;
    });
  },
  
  // Track story view
  async trackStoryView(storyId: string) {
    return retryOperation(async () => {
      const { error } = await supabase.rpc('increment_story_views', {
        story_id: storyId
      });
      
      if (error) {
        console.error('Error tracking story view:', error);
        throw error;
      }
      
      return true;
    });
  }
};

// Comments service
export const commentService = {
  // Get comments for a post
  async getComments(postId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.comments)
        .select('*, users:user_id(*)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error getting comments:', error);
        throw error;
      }
      
      return data;
    });
  },
  
  // Create comment
  async createComment(data: any) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to create a comment');
      }
      
      // Ensure user_id is set
      const commentData = {
        ...data,
        user_id: user.id
      };
      
      const { data: comment, error } = await supabase
        .from(collections.comments)
        .insert(commentData)
        .select('*, users:user_id(*)')
        .single();
      
      if (error) {
        console.error('Error creating comment:', error);
        throw error;
      }
      
      try {
        // Get commenter's username for the notification
        const { data: commenterProfile } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();
        
        const commenterUsername = commenterProfile?.username || 'Someone';
        
        // Check if this is a reply to another comment
        if (data.parent_comment_id) {
          // This is a reply to a comment
          // Get the parent comment to find its author
          const { data: parentComment } = await supabase
            .from(collections.comments)
            .select('user_id, post_id')
            .eq('id', data.parent_comment_id)
            .single();
          
          if (parentComment) {
            // Get the post to find its author
            const { data: postData } = await supabase
              .from(collections.posts)
              .select('user_id')
              .eq('id', parentComment.post_id)
              .single();
            
            // Notify the original commenter (if not the same as the replier)
            if (parentComment.user_id !== user.id) {
              await supabase
                .from('notifications')
                .insert({
                  id: uuidv4(),
                  recipient_user_id: parentComment.user_id,
                  sender_user_id: user.id,
                  type: 'reply_comment',
                  content_preview: `${commenterUsername} replied to your comment`,
                  related_entity_type: 'comment',
                  related_entity_id: comment.id,
                  is_read: false,
                  created_at: new Date().toISOString()
                });
            }
            
            // Notify the post owner (if different from commenter and parent commenter)
            if (postData && postData.user_id !== user.id && postData.user_id !== parentComment.user_id) {
              await supabase
                .from('notifications')
                .insert({
                  id: uuidv4(),
                  recipient_user_id: postData.user_id,
                  sender_user_id: user.id,
                  type: 'comment_post',
                  content_preview: `${commenterUsername} commented on your post`,
                  related_entity_type: 'post',
                  related_entity_id: parentComment.post_id,
                  is_read: false,
                  created_at: new Date().toISOString()
                });
            }
          }
        } else {
          // This is a direct comment on a post
          // Get the post to find its author
          const { data: postData } = await supabase
            .from(collections.posts)
            .select('user_id')
            .eq('id', data.post_id)
            .single();
          
          // Notify the post owner (if not the same as the commenter)
          if (postData && postData.user_id !== user.id) {
            await supabase
              .from('notifications')
              .insert({
                id: uuidv4(),
                recipient_user_id: postData.user_id,
                sender_user_id: user.id,
                type: 'comment_post',
                content_preview: `${commenterUsername} commented on your post`,
                related_entity_type: 'post',
                related_entity_id: data.post_id,
                is_read: false,
                created_at: new Date().toISOString()
              });
          }
        }
      } catch (notificationError) {
        // Log but don't fail the comment creation if notification fails
        console.error('Error creating comment notification:', notificationError);
      }
      
      return comment;
    });
  },
  
  // Delete comment
  async deleteComment(commentId: string) {
    return retryOperation(async () => {
      const { error } = await supabase
        .from(collections.comments)
        .delete()
        .eq('id', commentId);
      
      if (error) {
        console.error('Error deleting comment:', error);
        throw error;
      }
      
      return true;
    });
  }
};

// Likes service using Supabase database
export const likeService = {
  // Like content (add to post_likes table)
  async likeContent(contentId: string, contentType: string) {
    try {
      const user = await userService.getCurrentUser();
      if (!user) throw new Error('User must be logged in to like content');
      
      // Check if already liked
      const existingLike = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', contentId)
        .eq('user_id', user.id)
        .single();
      
      if (existingLike.data) {
        console.log(`Already liked ${contentType} ${contentId}`);
        return existingLike.data;
      }
      
      // Add the like
      const { data, error } = await supabase
        .from('post_likes')
        .insert([{
          post_id: contentId,
          user_id: user.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Update the post's like count
      await supabase.rpc('increment_post_likes', { post_id: contentId });
      
      console.log(`Liked ${contentType} ${contentId}`);
      return data;
    } catch (error) {
      console.error('Error liking content:', error);
      throw error;
    }
  },

  // Unlike content (remove from post_likes table)
  async unlikeContent(contentId: string) {
    try {
      const user = await userService.getCurrentUser();
      if (!user) throw new Error('User must be logged in to unlike content');
      
      // Remove the like
      const { error } = await supabase
        .from('post_likes')
        .delete()
        .eq('post_id', contentId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Update the post's like count
      await supabase.rpc('decrement_post_likes', { post_id: contentId });
      
      console.log(`Unliked post ${contentId}`);
    } catch (error) {
      console.error('Error unliking content:', error);
      throw error;
    }
  },

  // Check if user has liked content
  async hasLiked(contentId: string): Promise<boolean> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) return false;
      
      const { data, error } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', contentId)
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error checking like status:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      console.error('Error checking like status:', error);
      return false;
    }
  }
};

// Follow service
export const followService = {
  // Follow user
  async followUser(userId: string) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to follow another user');
      }
      
      if (user.id === userId) {
        throw new Error('Cannot follow yourself');
      }
      
      const { data, error } = await supabase
        .from(collections.follows)
        .insert({
          follower_id: user.id,
          following_id: userId
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error following user:', error);
        throw error;
      }
      
      // Create a notification for the user being followed
      try {
        // Get follower's username for the notification content
        const { data: followerProfile } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();
        
        const followerUsername = followerProfile?.username || 'Someone';
        
        // Create the notification
        await supabase
          .from('notifications')
          .insert({
            id: uuidv4(),
            recipient_user_id: userId,
            sender_user_id: user.id,
            type: 'follow_user',
            content_preview: `${followerUsername} started following you`,
            is_read: false,
            created_at: new Date().toISOString()
          });
      } catch (notificationError) {
        // Log but don't fail the follow operation if notification creation fails
        console.error('Error creating follow notification:', notificationError);
      }
      
      return data;
    });
  },
  
  // Unfollow user
  async unfollowUser(userId: string) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to unfollow another user');
      }
      
      const { error } = await supabase
        .from(collections.follows)
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', userId);
      
      if (error) {
        console.error('Error unfollowing user:', error);
        throw error;
      }
      
      return true;
    });
  },
  
  // Check if user is following another user
  async isFollowing(userId: string) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return false;
      }
      
      try {
        const { data, error } = await supabase
          .from(collections.follows)
          .select('id')
          .eq('follower_id', user.id)
          .eq('following_id', userId)
          .single();
        
        if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
          // If it's not a "no rows" error, but some other error, log it but don't throw
          console.log('Info: Follow status check result:', error.code);
        }
        
        return !!data;
      } catch (err: any) { // Type as any to access code property
        // Handle case where follows table doesn't exist yet
        if (err && err.code === '42P01') { // PostgreSQL error for undefined_table
          console.log('Info: Follows table does not exist yet, defaulting to not following');
          return false;
        }
        console.log('Warning: Error in isFollowing check, defaulting to not following:', err);
        return false;
      }
    });
  },
  
  // Get followers
  async getFollowers(userId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.follows)
        .select('follower:follower_id(*)')
        .eq('following_id', userId);
      
      if (error) {
        console.error('Error getting followers:', error);
        throw error;
      }
      
      return data.map(item => item.follower);
    });
  },
  
  // Get following
  async getFollowing(userId: string) {
    return retryOperation(async () => {
      const { data, error } = await supabase
        .from(collections.follows)
        .select('following:following_id(*)')
        .eq('follower_id', userId);
      
      if (error) {
        console.error('Error getting following:', error);
        throw error;
      }
      
      return data.map(item => item.following);
    });
  }
};

// Bookmark service using local storage for demo purposes
export const bookmarkService = {
  // Local storage key
  STORAGE_KEY: 'mozo_bookmarks',
  
  // Get bookmarks from local storage
  getStoredBookmarks(): Record<string, any> {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : {};
    } catch (error) {
      console.error('Error reading bookmarks from local storage:', error);
      return {};
    }
  },
  
  // Save bookmarks to local storage
  saveBookmarks(bookmarks: Record<string, any>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(bookmarks));
    } catch (error) {
      console.error('Error saving bookmarks to local storage:', error);
    }
  },
  
  // Bookmark a post
  async bookmarkPost(postId: string): Promise<void> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) throw new Error('User not authenticated');
      
      const bookmarks = this.getStoredBookmarks();
      
      // Initialize user's bookmarks if they don't exist
      if (!bookmarks[user.id]) {
        bookmarks[user.id] = {};
      }
      
      // Add the bookmark with timestamp
      bookmarks[user.id][postId] = {
        id: uuidv4(),
        post_id: postId,
        created_at: new Date().toISOString()
      };
      
      this.saveBookmarks(bookmarks);
      console.log(`Bookmarked post ${postId}`);
    } catch (error) {
      console.error('Error bookmarking post:', error);
      throw error;
    }
  },
  
  // Remove bookmark
  async unbookmarkPost(postId: string): Promise<void> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) throw new Error('User not authenticated');
      
      const bookmarks = this.getStoredBookmarks();
      
      // Check if user has bookmarks
      if (bookmarks[user.id] && bookmarks[user.id][postId]) {
        // Remove the bookmark
        delete bookmarks[user.id][postId];
        this.saveBookmarks(bookmarks);
        console.log(`Removed bookmark for post ${postId}`);
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  },
  
  // Check if user has bookmarked a post
  async hasBookmarked(postId: string): Promise<boolean> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) return false;
      
      const bookmarks = this.getStoredBookmarks();
      return !!(bookmarks[user.id] && bookmarks[user.id][postId]);
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  },
  
  // Get all bookmarked posts for the current user
  async getBookmarkedPosts(): Promise<any[]> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) return [];
      
      const bookmarks = this.getStoredBookmarks();
      const userBookmarks = bookmarks[user.id] || {};
      
      // For demo purposes, fetch some posts from the feed to use as bookmarked posts
      const { data: posts } = await supabase
        .from('posts')
        .select('*')
        .limit(10);
      
      if (!posts || posts.length === 0) return [];
      
      // Filter posts that are bookmarked
      const bookmarkedPostIds = Object.keys(userBookmarks);
      
      // If we have real bookmarked posts, use them
      if (bookmarkedPostIds.length > 0) {
        return bookmarkedPostIds.map(postId => ({
          id: userBookmarks[postId].id,
          post_id: postId,
          created_at: userBookmarks[postId].created_at,
          posts: posts.find(p => p.id === postId) || posts[0] // Fallback to first post if not found
        }));
      }
      
      // If no bookmarks yet, return empty array
      return [];
    } catch (error) {
      console.error('Error fetching bookmarked posts:', error);
      return [];
    }
  },
};

// Advertisement tracking service
export const adService = {
  // Track advertisement view
  async trackAdView(
    postId: string, 
    adType: string = 'slide_ad', 
    slideIndex: number = 2
  ): Promise<void> {
    try {
      const user = await userService.getCurrentUser();
      if (!user) {
        console.log('User not authenticated, skipping ad view tracking');
        return;
      }

      // Get user agent and IP info
      const userAgent = navigator.userAgent;
      
      // Call the database function to track the ad view
      const { error } = await supabase.rpc('track_ad_view', {
        p_post_id: postId,
        p_ad_type: adType,
        p_slide_index: slideIndex,
        p_user_agent: userAgent
      });

      if (error) {
        console.error('Error tracking ad view:', error);
      } else {
        console.log(`Ad view tracked: ${adType} on slide ${slideIndex} for post ${postId}`);
      }
    } catch (error) {
      console.error('Error in trackAdView:', error);
    }
  },

  // Get ad view statistics for a post (for post owners)
  async getAdViewStats(postId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('ad_views')
        .select('*')
        .eq('post_id', postId)
        .order('viewed_at', { ascending: false });

      if (error) {
        console.error('Error fetching ad view stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAdViewStats:', error);
      return [];
    }
  },

  // Get total ad views count for a post
  async getAdViewCount(postId: string, adType: string = 'slide_ad'): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('ad_views')
        .select('id', { count: 'exact' })
        .eq('post_id', postId)
        .eq('ad_type', adType);

      if (error) {
        console.error('Error getting ad view count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error getting ad view count:', error);
      return 0;
    }
  },

  // Get total ad views for all user's posts
  async getUserTotalAdViews(userId: string): Promise<number> {
    try {
      // First get all post IDs for the user
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('id')
        .eq('user_id', userId);

      if (postsError || !posts || posts.length === 0) {
        return 0;
      }

      // Extract post IDs
      const postIds = posts.map(post => post.id);

      // Get ad views for these posts
      const { data, error } = await supabase
        .from('ad_views')
        .select('id', { count: 'exact' })
        .in('post_id', postIds);

      if (error) {
        console.error('Error getting user total ad views:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error getting user total ad views:', error);
      return 0;
    }
  }
};

// Profile Banner Advertisement tracking service
export const profileBannerService = {
  // Track banner view (unified function for both profile and cover banners)
  async trackBannerView(
    profileId: string, 
    bannerType: 'profile_banner' | 'cover_banner' = 'profile_banner'
  ): Promise<void> {
    try {
      // Validate profileId is a valid UUID (skip placeholder users)
      if (!isValidUUID(profileId)) {
        return; // Skip tracking for placeholder users
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.id === profileId) return; // Don't track own profile views

      // Use a single unified RPC function to avoid overloading conflicts
      const { error } = await supabase.rpc('track_banner_view', {
        p_profile_id: profileId,
        p_viewer_id: user.id,
        p_banner_type: bannerType,
        p_ip_address: null,
        p_user_agent: navigator.userAgent
      });

      if (error) {
        console.error('Error tracking banner view:', error);
      }
    } catch (error) {
      console.error('Error in trackBannerView:', error);
    }
  },

  // Legacy wrapper for profile banner tracking
  async trackProfileBannerView(
    profileId: string, 
    bannerType: string = 'profile_banner'
  ): Promise<void> {
    return this.trackBannerView(profileId, bannerType as 'profile_banner' | 'cover_banner');
  },
  
  // Get profile banner view statistics
  async getProfileBannerViewStats(profileId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('profile_banner_views')
        .select('*')
        .eq('profile_id', profileId)
        .order('viewed_at', { ascending: false });

      if (error) {
        console.error('Error getting profile banner view stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getProfileBannerViewStats:', error);
      return [];
    }
  },

  // Get total profile banner views count
  async getProfileBannerViewCount(profileId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('profile_banner_views')
        .select('id', { count: 'exact' })
        .eq('profile_id', profileId);

      if (error) {
        console.error('Error getting profile banner view count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error getting profile banner view count:', error);
      return 0;
    }
  }
};

// Cover Banner Advertisement tracking service (for users without cover images)
export const coverBannerService = {
  // Track cover banner view using unified function
  async trackCoverBannerView(
    profileId: string, 
    bannerType: string = 'cover_banner'
  ): Promise<void> {
    return profileBannerService.trackBannerView(profileId, bannerType as 'profile_banner' | 'cover_banner');
  },
  
  // Get cover banner view statistics
  async getCoverBannerViewStats(profileId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('cover_banner_views')
        .select('*')
        .eq('profile_id', profileId)
        .order('viewed_at', { ascending: false });

      if (error) {
        console.error('Error getting cover banner view stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCoverBannerViewStats:', error);
      return [];
    }
  },

  // Get total cover banner views count
  async getCoverBannerViewCount(profileId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('cover_banner_views')
        .select('id', { count: 'exact' })
        .eq('profile_id', profileId);

      if (error) {
        console.error('Error getting cover banner view count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error getting cover banner view count:', error);
      return 0;
    }
  }
};

// Analytics page tracking service
export const analyticsService = {
  // Track analytics page view
  async trackAnalyticsPageView(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.rpc('track_analytics_page_view', {
        p_user_id: user.id,
        p_ip_address: null, // Could be populated with actual IP
        p_user_agent: navigator.userAgent,
        p_session_id: null // Could be populated with session ID
      });

      if (error) {
        console.error('Error tracking analytics page view:', error);
      }
    } catch (error) {
      console.error('Error in trackAnalyticsPageView:', error);
    }
  },

  // Get comprehensive user analytics
  async getUserAnalytics(userId: string): Promise<{
    profileViews: number;
    postViews: number;
    adViews: number;
    profileBannerViews: number;
    coverBannerViews: number;
    totalEngagement: number;
    analyticsPageViews: number;
  }> {
    try {
      const { data, error } = await supabase.rpc('get_user_analytics', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error getting user analytics:', error);
        return {
          profileViews: 0,
          postViews: 0,
          adViews: 0,
          profileBannerViews: 0,
          coverBannerViews: 0,
          totalEngagement: 0,
          analyticsPageViews: 0
        };
      }

      const result = data?.[0] || {};
      return {
        profileViews: result.profile_views || 0,
        postViews: result.post_views || 0,
        adViews: result.ad_views || 0,
        profileBannerViews: result.profile_banner_views || 0,
        coverBannerViews: result.cover_banner_views || 0,
        totalEngagement: result.total_engagement || 0,
        analyticsPageViews: result.analytics_page_views || 0
      };
    } catch (error) {
      console.error('Error in getUserAnalytics:', error);
      return {
        profileViews: 0,
        postViews: 0,
        adViews: 0,
        profileBannerViews: 0,
        coverBannerViews: 0,
        totalEngagement: 0,
        analyticsPageViews: 0
      };
    }
  }
};

// Followers/Following service for profile modals
export const followersService = {
  // Get followers list for a user
  async getFollowers(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('follows')
        .select(`
          follower_id,
          users!follower_id (id,
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('following_id', userId);

      if (error) {
        console.error('Error fetching followers:', error);
        return [];
      }

      // Transform data to match expected format
      return (data || []).map((follow: any) => ({
        id: follow.users?.id || follow.follower_id,
        username: follow.users?.username || 'unknown',
        full_name: follow.users?.full_name || null,
        avatar_url: follow.users?.avatar_url || null,
        isFollowing: false // Will be determined by checking current user's follows
      }));
    } catch (error) {
      console.error('Error in getFollowers:', error);
      return [];
    }
  },

  // Get following list for a user
  async getFollowing(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('follows')
        .select(`
          following_id,
          users!following_id (id,
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('follower_id', userId);

      if (error) {
        console.error('Error fetching following:', error);
        return [];
      }

      // Transform data to match expected format
      return (data || []).map((follow: any) => ({
        id: follow.users?.id || follow.following_id,
        username: follow.users?.username || 'unknown',
        full_name: follow.users?.full_name || null,
        avatar_url: follow.users?.avatar_url || null,
        isFollowing: true // These are people the user is following
      }));
    } catch (error) {
      console.error('Error in getFollowing:', error);
      return [];
    }
  },

  // Check if current user is following any of the users in a list
  async checkFollowStatus(userIds: string[]): Promise<{ [key: string]: boolean }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || userIds.length === 0) return {};

      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .in('following_id', userIds);

      if (error) {
        console.error('Error checking follow status:', error);
        return {};
      }

      // Create a map of userId -> isFollowing
      const followMap: { [key: string]: boolean } = {};
      userIds.forEach(id => followMap[id] = false);
      (data || []).forEach(follow => {
        followMap[follow.following_id] = true;
      });

      return followMap;
    } catch (error) {
      console.error('Error in checkFollowStatus:', error);
      return {};
    }
  }
};

// Helper function to validate UUID
function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

export default {
  userService,
  mediaService,
  postService,
  storyService,
  commentService,
  likeService,
  followService,
  bookmarkService,
  adService,
  profileBannerService,
  coverBannerService,
  analyticsService,
  followersService,
  collections,
  buckets,
  retryOperation
};
